import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';

interface CalorieCardProps {
  consumed: number;
  goal: number;
  remaining: number;
  protein?: { consumed: number; goal: number };
  carbs?: { consumed: number; goal: number };
  fat?: { consumed: number; goal: number };
}

export default function CalorieCard({
  consumed,
  goal,
  remaining,
  protein,
  carbs,
  fat,
}: CalorieCardProps) {
  const percentage = goal > 0 ? (consumed / goal) * 100 : 0;
  const isOverGoal = consumed > goal;

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Calorii Astăzi</h3>
          <p className="text-sm text-gray-600">
            {remaining > 0 
              ? `Mai ai ${remaining} kcal disponibile`
              : `Ai depășit cu ${Math.abs(remaining)} kcal`
            }
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between gap-6">
        {/* Circular Progress */}
        <div className="w-32 h-32">
          <CircularProgressbar
            value={percentage}
            maxValue={100}
            text={`${Math.round(percentage)}%`}
            styles={buildStyles({
              textSize: '20px',
              pathColor: isOverGoal ? '#ef4444' : '#10b981',
              textColor: isOverGoal ? '#ef4444' : '#10b981',
              trailColor: '#e5e7eb',
            })}
          />
        </div>

        {/* Stats */}
        <div className="flex-1 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Consumate</span>
            <span className="text-xl font-bold text-gray-900">{consumed}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Obiectiv</span>
            <span className="text-lg font-semibold text-gray-600">{goal}</span>
          </div>
          <div className={`flex items-center justify-between pt-2 border-t ${isOverGoal ? 'border-red-100' : 'border-green-100'}`}>
            <span className="text-sm font-medium text-gray-700">Rămase</span>
            <span className={`text-lg font-bold ${isOverGoal ? 'text-red-600' : 'text-green-600'}`}>
              {remaining > 0 ? remaining : 0}
            </span>
          </div>
        </div>
      </div>

      {/* Macronutrients */}
      {(protein || carbs || fat) && (
        <div className="mt-6 pt-6 border-t border-gray-100">
          <h4 className="text-sm font-semibold text-gray-900 mb-4">Macronutrienți</h4>
          <div className="space-y-3">
            {protein && (
              <MacroBar
                label="Proteine"
                consumed={protein.consumed}
                goal={protein.goal}
                color="bg-blue-500"
                unit="g"
              />
            )}
            {carbs && (
              <MacroBar
                label="Carbo"
                consumed={carbs.consumed}
                goal={carbs.goal}
                color="bg-yellow-500"
                unit="g"
              />
            )}
            {fat && (
              <MacroBar
                label="Grăsimi"
                consumed={fat.consumed}
                goal={fat.goal}
                color="bg-orange-500"
                unit="g"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

interface MacroBarProps {
  label: string;
  consumed: number;
  goal: number;
  color: string;
  unit: string;
}

function MacroBar({ label, consumed, goal, color, unit }: MacroBarProps) {
  const percentage = goal > 0 ? Math.min((consumed / goal) * 100, 100) : 0;

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium text-gray-700">{label}</span>
        <span className="text-xs text-gray-600">
          {Math.round(consumed)}/{goal}{unit}
        </span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className={`${color} h-2 rounded-full transition-all duration-300`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}
